/* eslint-disable @typescript-eslint/no-explicit-any */
import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { BehaviorSubject, catchError, filter, Observable, switchMap, take, throwError } from 'rxjs';
import { toasterService } from '../UIServices/toaster.service';
import { TranslateService } from '@ngx-translate/core';
import { BasicConstants } from '../constants/basics-constant';
import { Router } from '@angular/router';
import { AuthService } from '../services/shared/Auth/auth.service';

// Token refresh state management
let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const ErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const _toastr = inject(toasterService);
  const _router = inject(Router);
  const _auth = inject(AuthService);
  const _translate = inject(TranslateService);

  let messages: any = null;

  return next(req).pipe(
    catchError((error) => {
      if (error instanceof HttpErrorResponse) {
        // Handle 401 separately for token refresh
        if (error.status === 401) {
          return handle401Error(req, next, _toastr, _router, _auth, _translate);
        }

        // Handle other errors
        handleHttpError(error, _toastr, _router, _translate);

      }
      console.error('HTTP Error:', error.error);
      return throwError(() => error?.error?.detail);
    }),
  );
}

/**
 * Handles 401 Unauthorized errors with token refresh logic
 */
function handle401Error(req: HttpRequest<any>, next: HttpHandlerFn, toastr: toasterService, router: Router, auth: AuthService, translate: TranslateService): Observable<HttpEvent<any>> {
  // Prevent multiple simultaneous refresh requests
  if (isRefreshing) {
    // Wait for token refresh to complete
    return refreshTokenSubject.pipe(filter((token) => token !== null), take(1), switchMap((token) => {
      const clonedRequest = addToken(req, token ?? '');
      return next(clonedRequest);
    })
    );
  } else {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    // Otherwise start a refresh
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const refreshToken = localStorage.getItem(BasicConstants.USER_REFRESH_TOKEN);
    const refreshTokenExpiry = localStorage.getItem(BasicConstants.USER_REFRESH_TOKEN_EXPIRY);

    if (shouldLogoutDueToMissingOrExpiredRefreshToken(refreshToken, refreshTokenExpiry)) {
      return handleRefreshFailure(toastr, router, translate);
    }

    return auth.getRefreshToken().pipe(
      switchMap((res: any) => {
        isRefreshing = false;

        const token = res?.token || res?.Token;
        // Notify waiting requests
        refreshTokenSubject.next(token);

        // Retry original request with new token
        const clonedRequest = addToken(req, token);
        return next(clonedRequest);
      }),
      catchError((err) => {
        isRefreshing = false;
        return handleRefreshFailure(toastr, router, translate);
      })
    );
  }
}

/**
 * Adds authorization token to request
 */
function addToken(req: HttpRequest<any>, token: string): HttpRequest<any> {
  return req.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });
}

/**
 * Handles refresh token failure
 */
function handleRefreshFailure(toastr: toasterService, router: Router, translate: TranslateService) {
  isRefreshing = false;
  refreshTokenSubject.next(null);

  // Clear all auth data
  localStorage.removeItem(BasicConstants.USER_TOKEN);
  localStorage.removeItem(BasicConstants.USER_REFRESH_TOKEN);

  toastr.error(translate.instant('Toastr.Unauthorized'));
  router.navigate(['/auth']);

  return throwError(() => new Error('Session expired'));
}

/**
 * Handles different HTTP error responses
 */
function handleHttpError(error: HttpErrorResponse, toastr: toasterService, router: Router, translate: TranslateService): void {
  switch (error.status) {
    case 0:
      toastr.error(translate.instant('Toastr.ServerConnectionFailed'));
      break;

    case 400:
      // Bulk upload dialog renders row errors in a table; skip the generic toaster.
      if (hasBulkUploadRowErrors(error)) {
        break;
      }
      handleBadRequest(error, toastr, translate);
      break;

    case 403:
      toastr.error(translate.instant('Toastr.Forbidden'));
      break;

    case 404:
      toastr.error(translate.instant('Toastr.NotFound'));
      break;

    case 503:
      toastr.error(translate.instant('Toastr.ServiceUnavailable'));
      break;

    default:
      if (error.status > 500) {
        toastr.error(translate.instant('Toastr.ServerError'));
      }
  }
}

/**
 * True when the 400 payload is a community master-data bulk upload result with row errors.
 * The upload dialog shows those errors; a second generic toaster would hide them.
 */
function hasBulkUploadRowErrors(error: HttpErrorResponse): boolean {
  const url = (error.url ?? '').toLowerCase();
  if (!url.includes('masterdatabulkupload')) {
    return false;
  }

  const body = error.error;
  const data = body?.detail?.data ?? body?.data;
  return Array.isArray(data?.errors) && data.errors.length > 0;
}

/**
 * Handles 400 Bad Request errors with validation messages
 */
function handleBadRequest(error: HttpErrorResponse, toastr: toasterService, translate: TranslateService): void {
  try {
    const body = error.error;
    // API ExceptionHandlingFilter wraps the real payload in `detail`
    const errors = body?.detail?.errors ?? body?.errors;

    if (errors && typeof errors === 'object') {
      const messages = Object.values(errors).flat().filter((msg): msg is string => typeof msg === 'string').join(' | ');

      if (messages) {
        toastr.error(messages);
        return;
      }
    }

    const detailMessage = typeof body?.detail === 'string' ? body.detail : body?.detail?.message;
    if (typeof detailMessage === 'string' && detailMessage.trim()) {
      toastr.error(detailMessage);
      return;
    }

    if (body?.message && typeof body.message === 'string') {
      toastr.error(body.message);
      return;
    }

    toastr.error(translate.instant('Toastr.ValidationError'));
  } catch {
    toastr.error(translate.instant('Toastr.ValidationError'));
  }
}

/**
 * Checks if the refresh token is expired
 */
function isRefreshTokenExpired(refreshTokenExpiry: string | null): boolean {
  if (!refreshTokenExpiry) return true;
  const date = new Date(refreshTokenExpiry);
  const utc = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
  console.log('Refresh token expiry check:', { refreshTokenExpiry, utc, now: new Date() });
  return new Date() > utc;
}

/**
 * Determines if logout should occur due to missing or expired refresh token
 */
function shouldLogoutDueToMissingOrExpiredRefreshToken(refreshToken: string | null, refreshTokenExpiry: string | null): boolean {
  return !refreshToken || isRefreshTokenExpired(refreshTokenExpiry);
}